import { v4 as uuidv4 } from 'uuid';
import { Vector2 } from '../utils/Vector2';
import { Enemy, EnemyType, StatusEffect } from '../types/GameTypes';
import { calculateEnemyHP, ENEMY_TYPE_DATA } from '../types/EnemyTypes';
import { Pathfinder } from '../maps/Pathfinder';
import { GAME_CONSTANTS } from '../utils/Constants';

export class EnemySystem {
  private enemies: Map<string, Enemy> = new Map();
  private pathfinder: Pathfinder;

  constructor(pathfinder: Pathfinder) {
    this.pathfinder = pathfinder;
  }

  createEnemy(
    type: EnemyType,
    position: Vector2,
    waveNumber: number,
    isSurvival: boolean = false,
    isElite: boolean = false
  ): Enemy {
    const data = ENEMY_TYPE_DATA[type];
    const hp = calculateEnemyHP(type, waveNumber, isSurvival);
    
    // Elite modifier
    const finalHp = isElite ? hp * 2.5 : hp;
    const finalSpeed = isElite ? data.baseSpeed * 1.15 : data.baseSpeed;
    const finalGoalDamage = isElite ? data.goalDamage + 1 : data.goalDamage;

    // Convert tile position to pixel position (centered in tile)
    const pixelPosition = Vector2.fromTileCentered(position.x, position.y, 32);

    const enemy: Enemy = {
      id: uuidv4(),
      type,
      position: pixelPosition,
      hp: Math.floor(finalHp),
      maxHp: Math.floor(finalHp),
      speed: finalSpeed * 32, // Convert to pixels per second (32 pixels per tile)
      baseSpeed: finalSpeed * 32,
      path: [],
      currentPathIndex: 0,
      statusEffects: [],
      isElite,
      goalDamage: finalGoalDamage,
      isPhased: false, // Temporary ghost state when stuck on building
      idleTime: 0, // Time spent stationary
      lastPosition: pixelPosition.clone(), // Track position for idle detection
    };

    this.enemies.set(enemy.id, enemy);
    return enemy;
  }

  setEnemyPath(enemy: Enemy, path: Vector2[]): void {
    // Convert tile coordinates to pixel coordinates (centered in tiles)
    enemy.path = path.map(tilePos => Vector2.fromTileCentered(tilePos.x, tilePos.y, 32));
    enemy.currentPathIndex = 0;
  }

  updateEnemy(enemy: Enemy, deltaTime: number, goal: Vector2, grid: any, blockingBuildings?: Vector2[]): void {
    // Update status effects
    this.updateStatusEffects(enemy, deltaTime);

    // Calculate effective speed (affected by slow) - needed for idle detection
    let effectiveSpeed = enemy.speed;
    for (const effect of enemy.statusEffects) {
      if (effect.type === 'slow') {
        effectiveSpeed *= (1 - effect.value);
      }
      if (effect.type === 'freeze') {
        effectiveSpeed = 0; // Frozen enemies don't move
      }
    }

    // Elite slow resistance (min 20% speed)
    if (enemy.isElite) {
      effectiveSpeed = Math.max(effectiveSpeed, enemy.baseSpeed * 0.2);
    }
    
    // Ensure speed is never negative
    effectiveSpeed = Math.max(0, effectiveSpeed);

    // Note: Flying and Ghost enemy types are not implemented
    // This code is kept for future implementation
    if (false && (enemy.type === 'flying' || enemy.type === 'ghost')) {
      if (effectiveSpeed > 0) {
        const goalPixel = Vector2.fromTileCentered(goal.x, goal.y, 32);
        const direction = goalPixel.subtract(enemy.position);
        const distance = direction.distance(Vector2.zero());
        const moveDistance = effectiveSpeed * deltaTime;
        
        if (distance <= moveDistance || distance < 1) {
          // Reached goal
          enemy.position = goalPixel.clone();
        } else {
          // Move directly towards goal
          const normalized = direction.normalize();
          enemy.position = enemy.position.add(normalized.multiply(moveDistance));
        }
        
        enemy.lastPosition = enemy.position.clone();
      }
      return; // Skip all path-based movement logic
    }

    // Track idle time for force move and block breaker mode
    // Track for all ground enemies, even without a path (needed for block breaker)
    if (effectiveSpeed > 0) {
      const positionChange = enemy.position.distance(enemy.lastPosition);
      const MOVEMENT_THRESHOLD = 0.5; // Consider moving if moved more than 0.5 pixels
      
      if (positionChange < MOVEMENT_THRESHOLD) {
        // Enemy is stationary, increment idle time
        enemy.idleTime += deltaTime;
      } else {
        // Enemy is moving, reset idle time
        enemy.idleTime = 0;
        enemy.lastPosition = enemy.position.clone();
      }
      
      // Force path recalculation if idle for 2+ seconds
      // BUT: Only if not at a global blocking building and not in block breaker mode
      if (enemy.path.length > 0 && enemy.idleTime >= 2.0 && !enemy.isBlockBreaker) {
        const currentTile = enemy.position.toTile(32);
        const isAtGlobalBlockingBuilding = blockingBuildings && blockingBuildings.some(b => 
          Math.abs(currentTile.x - b.x) + Math.abs(currentTile.y - b.y) <= 1
        );
        
        if (!isAtGlobalBlockingBuilding) {
          // Not at global blocking building - clear path so Game.ts can recalculate
                    enemy.path = [];
                    enemy.currentPathIndex = 0;
          enemy.idleTime = 0; // Reset to prevent immediate re-trigger
          // Game.ts will recalculate path in next update
                }
              }
    } else {
      // No path or frozen - reset idle time
      enemy.idleTime = 0;
    }


    // Path assignment is handled by Game.ts for all enemy types
    // EnemySystem only handles movement along the assigned path
    // Ensure lastPosition is initialized
    if (!enemy.lastPosition) {
      enemy.lastPosition = enemy.position.clone();
    }

    // Move along path
    if (enemy.path.length > 0 && enemy.currentPathIndex < enemy.path.length && effectiveSpeed > 0) {
      const target = enemy.path[enemy.currentPathIndex];
      const direction = target.subtract(enemy.position);
      const distance = direction.distance(Vector2.zero());
      const moveDistance = effectiveSpeed * deltaTime; // Speed is already in pixels per second
      
      // Debug: Log first movement for each enemy
      if (enemy.id && !(enemy as any)._hasMoved) {
        console.log(`[MOVE] Enemy ${enemy.id} starting movement: path.length=${enemy.path.length}, waypoint=${enemy.currentPathIndex}, speed=${effectiveSpeed.toFixed(1)}, deltaTime=${deltaTime.toFixed(4)}, moveDistance=${moveDistance.toFixed(2)}, distance=${distance.toFixed(2)}, pos=(${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)}), target=(${target.x.toFixed(1)}, ${target.y.toFixed(1)})`);
        (enemy as any)._hasMoved = true;
      }

      if (distance <= moveDistance || distance < 1) {
        // Reached current waypoint
        enemy.position = target.clone();
        enemy.lastPosition = enemy.position.clone(); // Update last position
        enemy.idleTime = 0; // Reset idle time when reaching waypoint (not stuck)
        enemy.currentPathIndex++;
        if (enemy.id && !(enemy as any)._waypointReached) {
          console.log(`[MOVE] Enemy ${enemy.id} reached waypoint ${enemy.currentPathIndex - 1}, advancing to ${enemy.currentPathIndex}`);
          (enemy as any)._waypointReached = true;
        }
      } else {
        // Move towards waypoint
        const normalized = direction.normalize();
        const newPosition = enemy.position.add(normalized.multiply(moveDistance));
        
        // Debug: Log movement details
        if (enemy.id && !(enemy as any)._moveDetailsLogged) {
          console.log(`[MOVE] Enemy ${enemy.id} moving: distance=${distance.toFixed(2)}, moveDistance=${moveDistance.toFixed(2)}, deltaTime=${deltaTime.toFixed(4)}, from=(${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)}), to=(${newPosition.x.toFixed(1)}, ${newPosition.y.toFixed(1)})`);
          (enemy as any)._moveDetailsLogged = true;
        }
        
        // Check if new position would be in a building (wall or tower)
        // (Flying and ghost already returned early, so only ground units reach here)
          const newTile = newPosition.toTile(32);
          if (newTile.y >= 0 && newTile.y < grid.length && 
              newTile.x >= 0 && newTile.x < grid[newTile.y].length) {
            const tile = grid[newTile.y][newTile.x];
          if ((tile.type === 'wall' || tile.type === 'tower') && !enemy.isPhased) {
            const buildingTile = new Vector2(newTile.x, newTile.y);
            
            // If enemy is in block breaker mode and this is their target building, stop here
            if (enemy.isBlockBreaker && enemy.blockBreakerTarget) {
              if (buildingTile.x === enemy.blockBreakerTarget.x && 
                  buildingTile.y === enemy.blockBreakerTarget.y) {
                // This is the target building - stop here to attack
                enemy.blockingBuildingTile = buildingTile;
                return; // Don't move, stay at building to attack
              } else {
                // Different building - clear path so Game.ts can recalculate around it
              enemy.path = [];
              enemy.currentPathIndex = 0;
                return; // Don't move, Game.ts will recalculate path
            }
            } else {
              // Not in block breaker mode - check if this is a GLOBAL blocking building
              const isGlobalBlockingBuilding = blockingBuildings && 
                blockingBuildings.some(b => b.x === buildingTile.x && b.y === buildingTile.y);
              
              if (isGlobalBlockingBuilding) {
                // GLOBAL BLOCKING BUILDING - stop here for block breaker mode
                enemy.blockingBuildingTile = buildingTile;
                // Mark that enemy needs repath (will be handled by Game.ts)
                (enemy as any)._needsRepath = true;
                return; // Don't move, Game.ts will handle block breaker activation
              } else {
                // LOCAL BLOCKING (not a global blocking building) - clear path to recalculate
                enemy.blockingBuildingTile = buildingTile;
                enemy.path = []; // Clear path so Game.ts can recalculate around this building
                enemy.currentPathIndex = 0;
                // Mark that enemy needs repath (will be handled by Game.ts)
                (enemy as any)._needsRepath = true;
                if (enemy.id && !(enemy as any)._blockedLogged) {
                  console.log(`[BLOCK] Enemy ${enemy.id} blocked by local building at tile (${buildingTile.x}, ${buildingTile.y})`);
                  (enemy as any)._blockedLogged = true;
                }
                return; // Don't move, Game.ts will recalculate path
              }
            }
          }
        }
        
        // Actually move the enemy
        const oldPosition = enemy.position.clone();
        enemy.position = newPosition;
        enemy.lastPosition = enemy.position.clone(); // Update last position after movement
        
        // Debug: Log successful movement
        if (enemy.id && !(enemy as any)._movedSuccessfully) {
          const movedDist = oldPosition.distance(enemy.position);
          if (movedDist > 0.1) {
            console.log(`[MOVE] Enemy ${enemy.id} moved successfully: ${movedDist.toFixed(2)} pixels, from=(${oldPosition.x.toFixed(1)}, ${oldPosition.y.toFixed(1)}), to=(${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)})`);
            (enemy as any)._movedSuccessfully = true;
          }
        }
      }
    } else if (enemy.path.length === 0 && effectiveSpeed > 0) {
      // Debug: Log when enemy has no path but should be moving
      // (Flying/ghost already returned early, so only ground units reach here)
      if (enemy.id && !(enemy as any)._noPathLogged) {
        console.log(`[WARN] Enemy ${enemy.id} has no path! pos=(${enemy.position.x.toFixed(1)}, ${enemy.position.y.toFixed(1)}), speed=${effectiveSpeed.toFixed(1)}`);
        (enemy as any)._noPathLogged = true;
      }
    } else if (enemy.path.length > 0 && enemy.currentPathIndex >= enemy.path.length && effectiveSpeed > 0) {
      // Debug: Log when enemy has reached end of path
      if (enemy.id && !(enemy as any)._pathEndLogged) {
        console.log(`[WARN] Enemy ${enemy.id} reached end of path! path.length=${enemy.path.length}, currentIndex=${enemy.currentPathIndex}`);
        (enemy as any)._pathEndLogged = true;
      }
    } else if (effectiveSpeed === 0) {
      // Debug: Log when enemy has 0 speed
      // (Flying/ghost already returned early, so only ground units reach here)
      if (enemy.id && !(enemy as any)._zeroSpeedLogged) {
        console.log(`[WARN] Enemy ${enemy.id} has 0 speed!`);
        (enemy as any)._zeroSpeedLogged = true;
      }
    }
  }

  takeDamage(enemy: Enemy, amount: number, damageType: 'physical' | 'elemental'): void {
    const data = ENEMY_TYPE_DATA[enemy.type];
    
    // Apply resistances
    let finalDamage = amount;
    let effectiveResistance = data.physicalResistance;
    
    // Check for armor reduction debuffs (from Cannon Upgrade B)
    if (damageType === 'physical' && effectiveResistance > 0) {
      for (const effect of enemy.statusEffects) {
        if (effect.type === 'armor_reduction') {
          // Reduce armor by the debuff amount (15% = 0.15)
          // If enemy has 40% resistance (0.4), reduced by 15% becomes 25% (0.25)
          effectiveResistance = Math.max(0, effectiveResistance - effect.value);
        }
      }
      finalDamage *= (1 - effectiveResistance);
    }
    
    // Apply elemental vulnerability
    if (damageType === 'elemental') {
      finalDamage *= data.elementalVulnerability;
    }

    enemy.hp -= Math.floor(finalDamage);
  }

  applyStatusEffect(enemy: Enemy, effect: StatusEffect): void {
    const data = ENEMY_TYPE_DATA[enemy.type];

    // Note: Flying and Ghost enemy types are not implemented
    // This code is kept for future implementation
    if (data.isFlying && (effect.type === 'slow' || effect.type === 'freeze')) {
      return;
    }

    if (data.isGhost && effect.type === 'freeze') {
      return;
    }

    // Check if effect already exists
    const existingIndex = enemy.statusEffects.findIndex(e => e.type === effect.type);
    
    if (effect.type === 'electrified' && existingIndex >= 0) {
      // Stack electrified
      const existing = enemy.statusEffects[existingIndex];
      if (existing.stacks && existing.stacks < 10) {
        existing.stacks = (existing.stacks || 1) + 1;
        existing.duration = Math.max(existing.duration, effect.duration);
      }
    } else if (existingIndex >= 0) {
      // Replace existing effect
      enemy.statusEffects[existingIndex] = { ...effect };
    } else {
      // Add new effect
      enemy.statusEffects.push({ ...effect });
    }
  }

  private updateStatusEffects(enemy: Enemy, deltaTime: number): void {
    enemy.statusEffects = enemy.statusEffects.filter(effect => {
      effect.duration -= deltaTime;
      
      // Apply burn damage
      if (effect.type === 'burn' && effect.duration > 0) {
        this.takeDamage(enemy, effect.value * deltaTime, 'elemental');
      }

      return effect.duration > 0;
    });
  }

  removeEnemy(enemyId: string): boolean {
    return this.enemies.delete(enemyId);
  }

  getEnemy(id: string): Enemy | undefined {
    return this.enemies.get(id);
  }

  getAllEnemies(): Enemy[] {
    return Array.from(this.enemies.values());
  }

  clear(): void {
    this.enemies.clear();
  }

  hasReachedGoal(enemy: Enemy, goal: Vector2, threshold: number = 24): boolean {
    // goal is in tile coordinates, convert to pixel (center of tile)
    const goalPixel = Vector2.fromTileCentered(goal.x, goal.y, 32);
    const distance = enemy.position.distance(goalPixel);
    return distance < threshold;
  }
}

